import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// Compound Interest Calculator — rate conversion (APR to APY etc.) as shown in image
export default function CompoundInterestCalc() {
  const [inRate, setInRate] = useState(6);
  const [inComp, setInComp] = useState(12); // monthly
  const [outComp, setOutComp] = useState(1); // annually
  const compOpts = [
    { val: 1, label: 'Annually (APY)' },
    { val: 2, label: 'Semi-Annually' },
    { val: 4, label: 'Quarterly' },
    { val: 12, label: 'Monthly (APR)' },
    { val: 52, label: 'Weekly' },
    { val: 365, label: 'Daily' },
    { val: 0, label: 'Continuously' },
  ];
  const c = useMemo(() => {
    const r = inRate/100;
    let effAnnual;
    if (inComp === 0) effAnnual = Math.exp(r) - 1;
    else effAnnual = Math.pow(1 + r/inComp, inComp) - 1;
    // Convert to output compound frequency
    let outRate;
    if (outComp === 0) outRate = Math.log(1 + effAnnual);
    else outRate = outComp * (Math.pow(1 + effAnnual, 1/outComp) - 1);
    return { outRate: outRate * 100, effAnnual: effAnnual * 100 };
  }, [inRate, inComp, outComp]);
  const inLabel = compOpts.find(o=>o.val===inComp)?.label || '';
  const outLabel = compOpts.find(o=>o.val===outComp)?.label || '';
  return (
    <CalcShell look="cnet" title="Compound Interest Calculator" subtitle="Convert interest rates between different compounding frequencies (APR ↔ APY)."
      about={<>
        <p><b>Compound interest</b> is interest calculated on both the initial principal and the accumulated interest from prior periods. The effective rate depends on how often interest is compounded.</p>
        <p><b>APR</b> is the nominal annual rate; <b>APY</b> is the effective annual yield. APY &gt; APR for any compounding frequency more often than annual.</p>
      </>}
    >
      <div className="p-6 bg-blue-50 rounded-xl text-center mb-6">
        <div className="text-lg text-slate-800"><b>{inRate}%</b> compound {inLabel.toLowerCase()} is equivalent to <b className="text-blue-700" data-testid="ci-result">{c.outRate.toFixed(5)}%</b> compound {outLabel.toLowerCase()}.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="field"><label>Input Interest</label><div className="flex gap-2"><input type="number" step="0.01" value={inRate} onChange={e=>setInRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Compound</label><select value={inComp} onChange={e=>setInComp(+e.target.value)}>{compOpts.map(o=><option key={o.val} value={o.val}>{o.label}</option>)}</select></div>
        </div>
        <div>
          <div className="field"><label>Output Interest</label><div className="flex gap-2"><input type="text" value={c.outRate.toFixed(5) + '%'} readOnly className="flex-1 !bg-slate-100"/></div></div>
          <div className="field"><label>Compound</label><select value={outComp} onChange={e=>setOutComp(+e.target.value)}>{compOpts.map(o=><option key={o.val} value={o.val}>{o.label}</option>)}</select></div>
        </div>
      </div>
      <StepsPanel steps={[
        `Effective annual yield (APY) = (1 + ${inRate}%/${inComp || '∞'})^${inComp || '∞'} − 1 = <b>${c.effAnnual.toFixed(5)}%</b>`,
        `Converted to ${outLabel}: <b>${c.outRate.toFixed(5)}%</b>`,
      ]}/>
    </CalcShell>
  );
}
